import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { map, Observable, of, switchMap } from 'rxjs';
import { ApiBaseService } from './api/api-base.service';
import ApiOptionsConfig from './api/api-options.config';
import { Planning, PlanningPayload, PlanningStatus } from '../models/entities/planning';
import {
  COMPANY_DOCUMENT_ERROR_MESSAGES,
  CompanyDocumentGroupPayload,
  CompanyDocumentPayload,
  CompanyDocumentRevisionPayload
} from '../models/entities/company-document';
import {
  RISK_ERROR_MESSAGES,
  RiskPayload,
  RiskTimeFramePayload
} from '../models/entities/risk';
import { MEETING_MINUTE_ERROR_MESSAGES, MeetingMinutePayload } from '../models/entities/meeting-minute';

const BASE_URL = '/activity-plannings';
const BASE_URL_CHANGE_STATUS = '/change-status';
const BASE_URL_REMINDER = '/reminder';
const BASE_URL_HISTORY = '/history';
const BASE_URL_ASSIGNABLE_USERS = '/assignable-users';

const BASE_URL_COMPANY_DOCUMENT_GROUPS = '/company-document-groups';
const BASE_URL_COMPANY_DOCUMENTS = '/company-documents';
const BASE_URL_COMPANY_DOCUMENT_REVISIONS = '/company-document-revisions';
const BASE_URL_ATTACHMENT = '/attachment';
const BASE_URL_REQUEST_CONFIRMATION = '/request-confirmation';
const BASE_URL_SIGN_REVIEWED = '/sign-reviewed';
const BASE_URL_SIGN_APPROVED = '/sign-approved';
const BASE_URL_MOVE_IN_USE = '/move-in-use';

const BASE_URL_RISKS = '/risks';
const BASE_URL_RISK_ACTION_PROPOSES = '/action-proposes';
const BASE_URL_RISK_TIME_FRAMES = '/risk-time-frames';

const BASE_URL_MEETING_MINUTES = '/meeting-minutes';

@Injectable({
  providedIn: 'root',
})
export class QualityManagementService extends ApiBaseService {

  constructor() {
    super();
  }


  listPlannings(options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(BASE_URL), options?.params);
  }

  detailPlanning(activityPlanningId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/${activityPlanningId}`), options?.params);
  }

  createPlanning(data: PlanningPayload, reminder?: string | null): Observable<any> {
    const created$ = this.POST(this.getUrl(BASE_URL), data);

    if (!reminder) {
      return created$;
    }

    return created$.pipe(
      switchMap((response: any) => {
        const activityPlanningId = response?.data?.activityPlanning?.activityPlanningId;

        if (!activityPlanningId) {
          return of(response);
        }
        return this.updatePlanningReminder(Number(activityPlanningId), reminder)
          .pipe(map(() => response));
      })
    );
  }

  updatePlanning(activityPlanningId: number, data: PlanningPayload): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL}/${activityPlanningId}`), data);
  }

  deletePlanning(activityPlanningId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL}/${activityPlanningId}`));
  }

  changePlanningStatus(activityPlanningId: number, targetStatus: PlanningStatus, data?: any): Observable<any> {
    return this.POST(
      this.getUrl(`${BASE_URL}/${activityPlanningId}${BASE_URL_CHANGE_STATUS}/${targetStatus}`),
      data ?? {}
    );
  }

  updatePlanningReminder(activityPlanningId: number, reminder: string | null): Observable<any> {
    return this.PATCH(this.getUrl(`${BASE_URL}/${activityPlanningId}${BASE_URL_REMINDER}`), { reminder });
  }

  postponePlanning(activityPlanningId: number, payload: PlanningPayload, postponedReason: string): Observable<any> {
    return this.updatePlanning(activityPlanningId, payload).pipe(
      switchMap(() => this.changePlanningStatus(activityPlanningId, 'POSTPONED', { postponedReason }))
    );
  }

  listPlanningHistory(activityPlanningId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/${activityPlanningId}${BASE_URL_HISTORY}`), options?.params);
  }

  listPlanningAssignableUsers(options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}${BASE_URL_ASSIGNABLE_USERS}`), options?.params);
  }

  listCompanyDocumentGroups(options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(BASE_URL_COMPANY_DOCUMENT_GROUPS), options?.params);
  }

  detailCompanyDocumentGroup(companyDocumentGroupId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(
      this.getUrl(`${BASE_URL_COMPANY_DOCUMENT_GROUPS}/${companyDocumentGroupId}`),
      options?.params
    );
  }

  createCompanyDocumentGroup(data: CompanyDocumentGroupPayload): Observable<any> {
    return this.POST(this.getUrl(BASE_URL_COMPANY_DOCUMENT_GROUPS), data);
  }

  updateCompanyDocumentGroup(companyDocumentGroupId: number, data: CompanyDocumentGroupPayload): Observable<any> {
    return this.PUT(
      this.getUrl(`${BASE_URL_COMPANY_DOCUMENT_GROUPS}/${companyDocumentGroupId}`),
      data
    );
  }

  deleteCompanyDocumentGroup(companyDocumentGroupId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL_COMPANY_DOCUMENT_GROUPS}/${companyDocumentGroupId}`));
  }

  listCompanyDocuments(options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(BASE_URL_COMPANY_DOCUMENTS), options?.params);
  }

  detailCompanyDocument(companyDocumentId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL_COMPANY_DOCUMENTS}/${companyDocumentId}`), options?.params);
  }

  createCompanyDocument(data: CompanyDocumentPayload): Observable<any> {
    return this.POST(this.getUrl(BASE_URL_COMPANY_DOCUMENTS), data);
  }

  updateCompanyDocument(companyDocumentId: number, data: CompanyDocumentPayload): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL_COMPANY_DOCUMENTS}/${companyDocumentId}`), data);
  }

  deleteCompanyDocument(companyDocumentId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL_COMPANY_DOCUMENTS}/${companyDocumentId}`));
  }

  nextCompanyDocumentCode(department: string, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(
      this.getUrl(`/options/company-document/departments/${department}/next-code`),
      options?.params
    );
  }

  listCompanyDocumentRevisions(companyDocumentId: number, options?: ApiOptionsConfig): Observable<any> {
    const params = (options?.params ?? new HttpParams()).set('companyDocumentId', companyDocumentId);
    return this.GET(this.getUrl(BASE_URL_COMPANY_DOCUMENT_REVISIONS), params);
  }

  detailCompanyDocumentRevision(companyDocumentRevisionId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(
      this.getUrl(`${BASE_URL_COMPANY_DOCUMENT_REVISIONS}/${companyDocumentRevisionId}`),
      options?.params
    );
  }

  createCompanyDocumentRevision(data: CompanyDocumentRevisionPayload): Observable<any> {
    return this.POST(this.getUrl(BASE_URL_COMPANY_DOCUMENT_REVISIONS), data);
  }

  updateCompanyDocumentRevision(companyDocumentRevisionId: number, data: CompanyDocumentRevisionPayload): Observable<any> {
    return this.PUT(
      this.getUrl(`${BASE_URL_COMPANY_DOCUMENT_REVISIONS}/${companyDocumentRevisionId}`),
      data
    );
  }

  deleteCompanyDocumentRevision(companyDocumentRevisionId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL_COMPANY_DOCUMENT_REVISIONS}/${companyDocumentRevisionId}`));
  }

  uploadCompanyDocumentRevisionAttachment(companyDocumentRevisionId: number, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    return this.POSTMULTIPART(
      this.getUrl(`${BASE_URL_COMPANY_DOCUMENT_REVISIONS}/${companyDocumentRevisionId}${BASE_URL_ATTACHMENT}`),
      formData
    );
  }

  deleteCompanyDocumentRevisionAttachment(companyDocumentRevisionId: number, attachmentId: number): Observable<any> {
    return this.DELETE(
      this.getUrl(`${BASE_URL_COMPANY_DOCUMENT_REVISIONS}/${companyDocumentRevisionId}${BASE_URL_ATTACHMENT}/${attachmentId}`)
    );
  }

  attachmentPublicUrl(attachmentUrl: string): Observable<string> {
    return this.GET(attachmentUrl).pipe(
      map((response: any) => this.readResponseKey(response, 'attachment')?.publicUrl ?? '')
    );
  }

  requestCompanyDocumentRevisionConfirmation(companyDocumentRevisionId: number): Observable<any> {
    return this.POST(
      this.getUrl(`${BASE_URL_COMPANY_DOCUMENT_REVISIONS}/${companyDocumentRevisionId}${BASE_URL_REQUEST_CONFIRMATION}`),
      {}
    );
  }

  signCompanyDocumentRevisionReviewed(companyDocumentRevisionId: number): Observable<any> {
    return this.POST(
      this.getUrl(`${BASE_URL_COMPANY_DOCUMENT_REVISIONS}/${companyDocumentRevisionId}${BASE_URL_SIGN_REVIEWED}`),
      {}
    );
  }

  signCompanyDocumentRevisionApproved(companyDocumentRevisionId: number): Observable<any> {
    return this.POST(
      this.getUrl(`${BASE_URL_COMPANY_DOCUMENT_REVISIONS}/${companyDocumentRevisionId}${BASE_URL_SIGN_APPROVED}`),
      {}
    );
  }

  moveCompanyDocumentRevisionInUse(companyDocumentRevisionId: number): Observable<any> {
    return this.POST(
      this.getUrl(`${BASE_URL_COMPANY_DOCUMENT_REVISIONS}/${companyDocumentRevisionId}${BASE_URL_MOVE_IN_USE}`),
      {}
    );
  }

  listRisks(options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(BASE_URL_RISKS), options?.params);
  }

  detailRisk(riskId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL_RISKS}/${riskId}`), options?.params);
  }

  createRisk(data: RiskPayload): Observable<any> {
    return this.POST(this.getUrl(BASE_URL_RISKS), data);
  }

  updateRisk(riskId: number, data: RiskPayload): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL_RISKS}/${riskId}`), data);
  }

  deleteRisk(riskId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL_RISKS}/${riskId}`));
  }

  uploadRiskActionProposeAttachment(riskId: number, riskActionProposeId: number, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    return this.POSTMULTIPART(
      this.getUrl(
        `${BASE_URL_RISKS}/${riskId}${BASE_URL_RISK_ACTION_PROPOSES}/${riskActionProposeId}${BASE_URL_ATTACHMENT}`
      ),
      formData
    );
  }

  listRiskTimeFrames(options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(BASE_URL_RISK_TIME_FRAMES), options?.params);
  }

  detailRiskTimeFrame(riskTimeFrameId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL_RISK_TIME_FRAMES}/${riskTimeFrameId}`), options?.params);
  }

  createRiskTimeFrame(data: RiskTimeFramePayload): Observable<any> {
    return this.POST(this.getUrl(BASE_URL_RISK_TIME_FRAMES), data);
  }

  updateRiskTimeFrame(riskTimeFrameId: number, data: RiskTimeFramePayload): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL_RISK_TIME_FRAMES}/${riskTimeFrameId}`), data);
  }

  deleteRiskTimeFrame(riskTimeFrameId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL_RISK_TIME_FRAMES}/${riskTimeFrameId}`));
  }

  riskErrorMessage(error: any, fallback: string): string {
    const description = error?.error?.description ?? error?.description ?? null;

    if (description && RISK_ERROR_MESSAGES[description]) {
      return RISK_ERROR_MESSAGES[description];
    }

    return fallback;
  }

  listMeetingMinutes(options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(BASE_URL_MEETING_MINUTES), options?.params);
  }

  detailMeetingMinute(meetingMinuteId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL_MEETING_MINUTES}/${meetingMinuteId}`), options?.params);
  }

  createMeetingMinute(data: MeetingMinutePayload): Observable<any> {
    return this.POST(this.getUrl(BASE_URL_MEETING_MINUTES), data);
  }

  updateMeetingMinute(meetingMinuteId: number, data: MeetingMinutePayload): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL_MEETING_MINUTES}/${meetingMinuteId}`), data);
  }

  deleteMeetingMinute(meetingMinuteId: number): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL_MEETING_MINUTES}/${meetingMinuteId}`));
  }

  meetingMinuteErrorMessage(error: any, fallback: string): string {
    const description = error?.error?.error?.description
      ?? error?.error?.description
      ?? error?.description
      ?? null;

    if (description && MEETING_MINUTE_ERROR_MESSAGES[description]) {
      return MEETING_MINUTE_ERROR_MESSAGES[description];
    }

    return fallback;
  }


  companyDocumentErrorMessage(error: any, fallback: string): string {
    const description = error?.error?.description ?? error?.description ?? null;

    if (description && COMPANY_DOCUMENT_ERROR_MESSAGES[description]) {
      return COMPANY_DOCUMENT_ERROR_MESSAGES[description];
    }

    return fallback;
  }

  readResponseKey(response: any, ...keys: string[]): any {
    if (!response) {
      return undefined;
    }

    for (const key of keys) {
      const value = response?.data?.[key] ?? response?.[key];
      if (value !== undefined && value !== null) {
        return value;
      }
    }

    return undefined;
  }

  toDateTimeLocal(value?: string | null): string {
    if (!value) {
      return '';
    }

    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
      return '';
    }

    const pad = (input: number) => String(input).padStart(2, '0');
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`
      + `T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
  }

  fromDateTimeLocal(value?: string | null): string | null {
    if (!value) {
      return null;
    }

    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
      return null;
    }

    return parsed.toISOString();
  }

  toDateInput(value?: string | null): string {
    if (!value) {
      return '';
    }

    const dateOnlyMatch = /^(\d{4}-\d{2}-\d{2})/.exec(value);
    if (dateOnlyMatch) {
      return dateOnlyMatch[1];
    }

    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
      return '';
    }

    const pad = (input: number) => String(input).padStart(2, '0');
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
  }

  fromDateInput(value?: string | null): string | null {
    return value ? value : null;
  }

  toTimeInput(value?: string | null): string {
    if (!value) {
      return '';
    }

    const match = /^(\d{2}):(\d{2})/.exec(value);
    return match ? `${match[1]}:${match[2]}` : '';
  }

  fromTimeInput(value?: string | null): string | null {
    if (!value) {
      return null;
    }

    const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
    if (!match) {
      return null;
    }

    return `${match[1]}:${match[2]}:${match[3] ?? '00'}`;
  }

  formatPlanningDateTime(value?: string | null): string {
    if (!value) {
      return '';
    }

    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
      return '';
    }

    const pad = (input: number) => String(input).padStart(2, '0');
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())} `
      + `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}:${pad(parsed.getSeconds())}`;
  }

  isPlanningCompleted(planning?: Planning | null): boolean {
    return planning?.status === 'COMPLETED';
  }

  dueDateAfterReminderValidator(dueDateKey: string, reminderKey: string): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const dueDateControl = group.get(dueDateKey);
      const reminderControl = group.get(reminderKey);

      if (!dueDateControl || !reminderControl) {
        return null;
      }

      const dueDate = dueDateControl.value ? new Date(dueDateControl.value) : null;
      const reminder = reminderControl.value ? new Date(reminderControl.value) : null;

      const existingErrors = { ...(dueDateControl.errors ?? {}) };
      delete existingErrors['backendError'];

      if (!dueDate || !reminder || isNaN(dueDate.getTime()) || isNaN(reminder.getTime())) {
        this.clearControlError(dueDateControl, 'backendError');
        return null;
      }

      if (reminder.getTime() > dueDate.getTime()) {
        dueDateControl.setErrors({
          ...existingErrors,
          backendError: "Due Date can't be earlier than the Reminder."
        });
        return { dueDateBeforeReminder: true };
      }

      this.clearControlError(dueDateControl, 'backendError');
      return null;
    };
  }

  private clearControlError(control: AbstractControl, errorKey: string): void {
    if (!control.errors || !control.errors[errorKey]) {
      return;
    }

    const errors = { ...control.errors };
    delete errors[errorKey];
    control.setErrors(Object.keys(errors).length ? errors : null);
  }
}
